import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchNotifications } from "../../../utils/api/fetchNotifications";
import { useNotification } from "./NotificationProvider";
import { putMarkAsReadNotifications } from "../../../utils/api/putMarkAsReadNotifications";
import { SiteContext } from "../../../providers/SiteProvider";
import { useOS } from "../../../hooks/useOS";
import { getSubdomain } from "../../../utils/getSubdomain";
import getImageUrl from "../../../utils/getImageUrl";
import PAGE_CONFIG from "../../../config/page";

const key = window.location.search.split("=")[1]?.startsWith("site")
  ? window.location.search.split("=")[1]
  : "";

export const useGetPreIconFavico = () => {
  const site = useContext(SiteContext);
  const subdomain = getSubdomain(window.location.hostname);
  const { isWindows, isAndroid } = useOS();
  const { favicoPC = "", favicoIOS = "" } = site ?? {};
  const appIconImageUrl = !key
    ? getImageUrl(
        subdomain,
        PAGE_CONFIG.IMAGE_URL_PATH.SITE,
        isWindows || isAndroid
          ? PAGE_CONFIG.FAVICON
          : PAGE_CONFIG.APPLE_TOUCH_ICON
      )
    : isWindows || isAndroid
    ? favicoPC
    : favicoIOS;

  return appIconImageUrl;
};

export const useMarkAsReadNotifications = () => {
  const { updateHasNotificationUnread } = useNotification();

  const markAsReadNotifications = useCallback(async () => {
    try {
      await putMarkAsReadNotifications().then((res) => {
        const { isSuccess } = res ?? {};
        if (!!isSuccess) {
          updateHasNotificationUnread(false);
        } else {
          throw new Error("");
        }
      });
    } catch (error) {
      console.log(error);
    }
  },[])
  return {
    markAsReadNotifications
  }
};

export const useGetNotification = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    fetchNotifications: getAllNotification,
    updateHasNotificationUnread,
  } = useNotification();

  useEffect(() => {
    const queryNotification = async () => {
      try {
        await fetchNotifications({
          limit: 10,
        }).then((res) => {
          if (res?.isSuccess) {
            const {
              count_un_read_items = 0,
              data = [],
              has_more,
              next_cursor,
            } = res?.result ?? {};
            getAllNotification(data, !!has_more, next_cursor);
            updateHasNotificationUnread(!!count_un_read_items);
          } else {
            throw new Error("");
          }
        });
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };
    queryNotification();
  }, []);

  return {
    isLoading
  };
};

export const useGetNextPage = () => {
  const [nextPageLoading, setNextPageLoading] = useState<boolean>(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const {
    state: { nextCursor, hasMore },
    fetchNotifications: getAllNotification,
  } = useNotification();

  const fetchNextPage = useCallback(async () => {
    if (nextPageLoading || !hasMore) return;

    setNextPageLoading(true);
    try {
      const response = await fetchNotifications({
        limit: 10,
        next_cursor: nextCursor,
      });

      if (response?.isSuccess) {
        const { data = [], has_more, next_cursor } = response?.result ?? {};
        getAllNotification(data, !!has_more, next_cursor);
      } else {
        throw new Error("Failed to fetch next page");
      }
    } catch (error) {
      console.error("Fetch next page error:", error);
    } finally {
      setNextPageLoading(false);
    }
  }, [nextCursor, nextPageLoading, hasMore]);

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (nextPageLoading || !hasMore) {
        if (observer.current) {
          observer.current.disconnect();
          observer.current = null;
        }
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      if (node) {
        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore && !nextPageLoading) {
            fetchNextPage();
          }
        });

        observer.current.observe(node);
      }
    },
    [nextPageLoading, hasMore, fetchNextPage]
  );

  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return { nextPageLoading, lastItemRef };
};
